import { Link } from "react-router-dom";
import {
  BookOpen, UserPlus, LogIn, Clock, ShieldCheck, Users, Wallet, Map,
  CalendarDays, Plane, FileText, Bell, Vote, Receipt, Home as HomeIcon,
  Check, X, Lock, Sparkles, ArrowRight, MapPin,
} from "lucide-react";
import { SITE } from "../lib/site";
import BrandContract from "../components/BrandContract";

/* Public (signed-out) landing page for /travel-hub — SaaS product page per the
   Feb-2026 site review: 5-minute onboarding promise, how it works, sample family
   trip, item-level security explanation and real pricing. */

const _P = "https://static.prod-images.emergentagent.com/jobs/db462b94-64d2-4f35-becf-95e1471a4dcf/images/";
const IMG = {
  roadtrip: _P + "e57fa366debbfd3dbe3b9dcab05346ff9447fa488d264a291165be05659116d8.jpeg",
};

/* Real product screenshots (captured from the live app, /public/screenshots). */
const SHOTS = [
  { src: "/screenshots/dayplan.jpg", alt: "Day-by-day itinerary board", label: "Day-by-day itinerary — drag & drop, co-edited by the family" },
  { src: "/screenshots/expenses.jpg", alt: "Trip expense tracker with settlement", label: "Trip expenses — budgets, currencies and who-owes-whom" },
  { src: "/screenshots/hub.jpg", alt: "My Travel Hub dashboard", label: "Your hub — weather, trips, documents and reminders" },
  { src: "/screenshots/planner.jpg", alt: "AI Trip Planner", label: "AI Trip Planner — a full itinerary from one sentence" },
];

const SUITE = [
  { icon: BookOpen, color: "text-blue-700 bg-blue-50 border-blue-200", title: "My Travel Hub", to: "/travel-hub",
    text: "Your personal travel organizer — trips, documents, packing, diaries, loyalty and reminders in one private place." },
  { icon: Users, color: "text-violet-700 bg-violet-50 border-violet-200", title: "GroupTrip Planner", to: "/grouptrip-planner",
    text: "Plan together — shared itineraries, group polls & voting, trip chat and friend suggestions with clear roles." },
  { icon: Receipt, color: "text-emerald-700 bg-emerald-50 border-emerald-200", title: "Trip Expense Manager", to: "/trip-expense-manager",
    text: "Budgets, receipts, multi-currency spending, cost splitting and who-owes-whom settlement for every trip." },
  { icon: HomeIcon, color: "text-amber-700 bg-amber-50 border-amber-200", title: "Family Expense Hub", to: "/family-expense-hub",
    text: "Everyday household spending — groceries, bills, rent — shared with your household, never mixed with travel." },
];

const STEPS = [
  { n: 1, title: "Create your account", text: "Sign up free — no credit card. Your 45-day trial starts instantly.", mins: "1 min" },
  { n: 2, title: "Add your first trip", text: "Type it, import a booking email, or let the AI Trip Planner build a full day-by-day plan from one sentence.", mins: "2 min" },
  { n: 3, title: "Invite your people", text: "Share the itinerary with family or friends — they see the plan, vote on ideas and chat. You control exactly what each person can see.", mins: "2 min" },
];

const FEATURES = [
  { icon: Sparkles, label: "AI Trip Planner — a full itinerary from one sentence" },
  { icon: CalendarDays, label: "Day-by-day plan board with drag & drop" },
  { icon: Plane, label: "Live flight status, gate alerts & boarding reminders" },
  { icon: FileText, label: "Documents vault — passports, visas, insurance" },
  { icon: Wallet, label: "Budgets, receipts & family settlement" },
  { icon: Bell, label: "Evening-before trip briefs with weather" },
  { icon: Map, label: "Offline trip kit — everything works without signal" },
  { icon: Vote, label: "Group polls, chat & activity suggestions" },
];

/* Item-level permission matrix — the security story in one glance.
   Columns: You (owner) / Editor / Financial manager / Viewer */
const PERM_ROWS = [
  ["Day-by-day itinerary", true, true, true, true],
  ["Group chat & polls", true, true, true, true],
  ["Add & edit plans", true, true, false, false],
  ["Expenses & settlement", true, true, true, false],
  ["Passports & documents", true, false, false, false],
];

const PLANS = [
  { id: "individual", label: "Individual", price: "$39", per: "/year", note: "Your complete personal travel workspace." },
  { id: "couple", label: "Couple", price: "$59", per: "/year", note: "Two accounts plus a shared travel hub." },
  { id: "family", label: "Family or Small Group", price: "$99", per: "/year", note: "Family of 5, up to 9 travelers total." },
  { id: "large_group", label: "Groups", price: "$199", per: "/year", note: "One private hub for up to 30 travelers." },
];

const FAQS = [
  ["Do I need a credit card to try it?", "No. Every account starts with a full-featured 45-day free trial — no card, and it never converts automatically."],
  ["Can people I share with see my passport or expenses?", "No. Sharing is item-level: an itinerary viewer sees only the plan. Documents and money stay private unless you explicitly grant them."],
  ["Does it work offline?", "Yes — save any trip for offline and the full plan, documents checklist and reminders work without signal."],
  ["What happens when my trial ends?", "Your data stays safe and readable forever. Subscribe only if you want to keep planning new trips."],
];

export default function TravelHubLanding() {
  return (
    <div data-testid="hub-landing">
      <BrandContract compact />
      {/* HERO */}
      <section className="relative border-b border-zinc-200">
        <div className="absolute inset-0"><img src={IMG.roadtrip} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-indigo-950/80" /></div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-20 text-white">
          <div className="text-xs font-mono uppercase tracking-widest text-blue-300 mb-3 flex items-center gap-2"><BookOpen className="w-4 h-4" /> My Travel Hub — by {SITE.name}</div>
          <h1 className="font-heading font-900 text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl" data-testid="hub-landing-title">
            Every trip, organized.<br />Every detail, private.
          </h1>
          <p className="text-white/85 text-lg mt-4 max-w-2xl">
            Plan trips with AI, keep documents safe, split costs with family and share
            itineraries with exactly the people you choose — all in one hub.
          </p>
          <div className="mt-5 inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-400/50 text-emerald-200 px-4 py-2 text-sm font-700" data-testid="hub-landing-5min">
            <Clock className="w-4 h-4" /> Set up in 5 minutes — first trip planned before your coffee cools
          </div>
          <div className="flex flex-wrap gap-3 mt-7">
            <Link to="/register" data-testid="hub-landing-signup"
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-7 py-3.5 font-700 hover:bg-blue-500 transition-colors">
              <UserPlus className="w-5 h-5" /> Start free — 45 days, no card
            </Link>
            <Link to="/login" data-testid="hub-landing-login"
              className="inline-flex items-center gap-2 bg-white/10 border border-white/30 text-white px-7 py-3.5 font-700 hover:bg-white/20 transition-colors">
              <LogIn className="w-5 h-5" /> Sign in
            </Link>
          </div>
          {/* real product screenshot in the hero */}
          <div className="mt-10 -mb-24 lg:-mb-32 max-w-4xl" data-testid="hub-landing-hero-shot">
            <img src={SHOTS[0].src} alt={SHOTS[0].alt}
              className="w-full border-4 border-white/20 shadow-2xl shadow-black/50 rounded-lg" />
          </div>
        </div>
      </section>
      <div className="h-24 lg:h-32" aria-hidden="true" />

      {/* PRODUCT SUITE */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-14" data-testid="hub-landing-suite">
        <div className="text-xs font-mono uppercase tracking-widest text-primary mb-2">One account · Four focused tools</div>
        <h2 className="font-heading font-900 text-2xl sm:text-3xl mb-6">Pick the tool for the job — they all work together</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {SUITE.map((p) => (
            <Link key={p.title} to={p.to} data-testid={`hub-landing-suite-${p.title.toLowerCase().replace(/\s/g, "-")}`}
              className="border border-zinc-300 bg-white p-5 hover:-translate-y-0.5 hover:border-primary hover:shadow-md transition-[transform,border-color,box-shadow] group">
              <span className={`inline-flex p-2.5 border ${p.color}`}><p.icon className="w-6 h-6" /></span>
              <div className="font-heading font-900 text-lg mt-3 group-hover:text-primary transition-colors">{p.title}</div>
              <p className="text-sm text-muted-foreground mt-1.5">{p.text}</p>
              <div className="mt-3 text-sm font-700 text-primary flex items-center gap-1">Learn more <ArrowRight className="w-4 h-4" /></div>
            </Link>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-zinc-50 border-y border-zinc-200" data-testid="hub-landing-how">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14">
          <h2 className="font-heading font-900 text-2xl sm:text-3xl mb-2">How it works</h2>
          <p className="text-muted-foreground mb-8">From nothing to a fully planned, shared trip — in about five minutes.</p>
          <div className="grid md:grid-cols-3 gap-5">
            {STEPS.map((s) => (
              <div key={s.n} className="border border-zinc-300 bg-white p-6 relative">
                <div className="absolute -top-3 left-5 bg-blue-700 text-white text-xs font-800 px-2.5 py-1">STEP {s.n} · {s.mins}</div>
                <div className="font-heading font-900 text-lg mt-2">{s.title}</div>
                <p className="text-sm text-zinc-700 mt-2">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SAMPLE FAMILY TRIP */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-14" data-testid="hub-landing-sample">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-primary mb-2">A real example</div>
            <h2 className="font-heading font-900 text-2xl sm:text-3xl">The Kumar family's India trip, planned in one evening</h2>
            <ul className="mt-5 space-y-3 text-sm text-zinc-700">
              <li className="flex gap-2.5"><Sparkles className="w-4 h-4 text-violet-600 mt-0.5 shrink-0" /><span><b>Mom asks the AI planner</b> for "2 weeks in Kerala with kids, relaxed pace" — a full day-by-day itinerary appears, ready to edit.</span></li>
              <li className="flex gap-2.5"><Users className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" /><span><b>She shares it with the family.</b> Grandparents get view access, Dad can edit, the teens vote on a beach-vs-backwaters poll.</span></li>
              <li className="flex gap-2.5"><Receipt className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /><span><b>Everyone logs what they spend</b> — receipts photographed, currencies converted, and settlement shows exactly who owes whom at the end.</span></li>
              <li className="flex gap-2.5"><Lock className="w-4 h-4 text-rose-600 mt-0.5 shrink-0" /><span><b>Passports stay locked in Mom's vault.</b> Nobody she shared the itinerary with can open documents or the money view.</span></li>
              <li className="flex gap-2.5"><Bell className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" /><span><b>Each evening on the trip</b> everyone gets tomorrow's plan with the weather — even offline in the backwaters.</span></li>
            </ul>
          </div>
          <div className="grid grid-cols-2 gap-3" data-testid="hub-landing-sample-shots">
            <figure className="col-span-2">
              <img src={SHOTS[1].src} alt={SHOTS[1].alt} loading="lazy" className="w-full object-cover border border-zinc-300 shadow-md" />
              <figcaption className="text-[11px] text-muted-foreground mt-1">{SHOTS[1].label}</figcaption>
            </figure>
            <figure>
              <img src={SHOTS[2].src} alt={SHOTS[2].alt} loading="lazy" className="w-full h-36 lg:h-44 object-cover object-top border border-zinc-300 shadow-md" />
              <figcaption className="text-[11px] text-muted-foreground mt-1">{SHOTS[2].label}</figcaption>
            </figure>
            <figure>
              <img src={SHOTS[3].src} alt={SHOTS[3].alt} loading="lazy" className="w-full h-36 lg:h-44 object-cover object-top border border-zinc-300 shadow-md" />
              <figcaption className="text-[11px] text-muted-foreground mt-1">{SHOTS[3].label}</figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="bg-zinc-50 border-y border-zinc-200">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14" data-testid="hub-landing-features">
          <h2 className="font-heading font-900 text-2xl sm:text-3xl mb-8">Everything a trip needs, nothing it doesn't</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURES.map((f) => (
              <div key={f.label} className="flex items-start gap-3 border border-zinc-300 bg-white p-4">
                <f.icon className="w-5 h-5 text-blue-700 mt-0.5 shrink-0" />
                <span className="text-sm font-600">{f.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECURITY — item-level permissions */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-14" data-testid="hub-landing-security">
        <div className="grid lg:grid-cols-2 gap-10">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-rose-700 mb-2 flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> Privacy by design</div>
            <h2 className="font-heading font-900 text-2xl sm:text-3xl">Share the plan. Never the passport.</h2>
            <p className="text-sm text-zinc-700 mt-4">
              Every item in your hub has its own permission. Sharing an itinerary shares <b>only the
              itinerary</b> — documents, expenses and personal notes stay locked to you unless you
              explicitly grant access, per person, per item.
            </p>
            <ul className="mt-4 space-y-2 text-sm text-zinc-700">
              <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> Roles per person: organizer, editor or viewer — change or revoke anytime</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> Share links can expire automatically and never include documents or booking references</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> Sensitive documents require step-up re-verification even on your own account</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> Photo uploads are scanned for boarding-pass codes and GPS data is stripped</li>
            </ul>
          </div>
          <div className="border border-zinc-300 bg-white p-5 self-start">
            <div className="font-heading font-800 text-sm uppercase tracking-wide mb-3">Who sees what when you share a trip</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-mono uppercase text-muted-foreground border-b border-zinc-200">
                  <th className="py-2 pr-2">Item</th><th className="py-2 px-1 text-center">You</th>
                  <th className="py-2 px-1 text-center">Editor</th>
                  <th className="py-2 px-1 text-center" title="Financial manager">Fin. mgr</th>
                  <th className="py-2 pl-1 text-center">Viewer</th>
                </tr>
              </thead>
              <tbody>
                {PERM_ROWS.map(([label, o, e, f, v]) => (
                  <tr key={label} className="border-b border-zinc-100 last:border-0">
                    <td className="py-2.5 pr-2 font-600">{label}</td>
                    {[o, e, f, v].map((yes, i) => (
                      <td key={i} className="py-2.5 px-1 text-center">
                        {yes ? <Check className="w-4 h-4 text-emerald-600 inline" /> : <X className="w-4 h-4 text-rose-400 inline" />}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[11px] text-muted-foreground mt-3">The <b>Financial manager</b> role lets one trusted person handle the money — expenses, receipts and settlement — without ever opening documents. And the expense summary itself has a shareable, embeddable read-only link.</p>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="bg-indigo-950 text-white" data-testid="hub-landing-pricing">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14">
          <h2 className="font-heading font-900 text-2xl sm:text-3xl mb-2">Simple annual pricing</h2>
          <p className="text-white/70 mb-8">Try everything free for 45 days — no card, no auto-conversion. Subscribe only if you love it.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 items-stretch">
            {PLANS.map((p) => (
              <div key={p.id} data-testid={`hub-landing-plan-${p.id}`}
                className="p-5 flex flex-col bg-white text-zinc-900 border border-white/20">
                <div className="font-heading font-900">{p.label}</div>
                <div className="mt-2"><span className="font-heading font-900 text-2xl">{p.price}</span><span className="text-xs text-muted-foreground"> {p.per}</span></div>
                <p className="text-xs text-zinc-600 mt-2 flex-1">{p.note}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <Link to="/register" data-testid="hub-landing-pricing-cta"
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 font-700 hover:bg-blue-500 transition-colors">
              <UserPlus className="w-5 h-5" /> Start your free 45 days
            </Link>
            <Link to="/membership" className="text-sm text-blue-300 hover:underline" data-testid="hub-landing-full-plans">Compare full plan details →</Link>
          </div>
        </div>
      </section>

      {/* FAQ + FINAL CTA */}
      <section className="max-w-5xl mx-auto px-5 lg:px-8 py-14" data-testid="hub-landing-faq">
        <h2 className="font-heading font-900 text-2xl sm:text-3xl mb-6">Common questions</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {FAQS.map(([q, a]) => (
            <div key={q} className="border border-zinc-300 bg-white p-5">
              <div className="font-heading font-800 text-base">{q}</div>
              <p className="text-sm text-zinc-700 mt-1.5">{a}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 text-center border border-dashed border-zinc-300 py-10">
          <MapPin className="w-7 h-7 mx-auto text-primary mb-2" />
          <p className="text-lg font-heading font-700 mb-3">Your next trip deserves a home</p>
          <Link to="/register" data-testid="hub-landing-final-cta"
            className="inline-flex items-center gap-2 bg-primary text-white px-7 py-3 font-700 hover:bg-blue-700 transition-colors">
            <UserPlus className="w-5 h-5" /> Create your free hub
          </Link>
        </div>
      </section>
    </div>
  );
}
